/**
 * useOfflineSync — Orchestrates offline queue sync lifecycle.
 *
 * Triggers sync when:
 *  1. App comes back online (online event)
 *  2. Service worker sends SYNC_REQUESTED message (Background Sync API, Chrome/Edge)
 *  3. Polling every 10s when online (Safari/Firefox fallback)
 *
 * Returns sync state for use in OfflineIndicator.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/lib/auth/AuthProvider';
import { useOnlineDetection } from './useOnlineDetection';
import { syncPendingItems, registerBackgroundSync, type SyncProgress } from '@/lib/offline/syncQueue';
import { offlineQueue } from '@/lib/offline/offlineQueue';

export interface OfflineSyncState {
  isSyncing: boolean;
  pendingCount: number;
  lastSyncAt: number | null;
  lastError: string | null;
  progress: SyncProgress | null;
}

const INITIAL_STATE: OfflineSyncState = {
  isSyncing: false,
  pendingCount: 0,
  lastSyncAt: null,
  lastError: null,
  progress: null,
};

export function useOfflineSync() {
  const { isOnline } = useOnlineDetection();
  const { firebaseUser } = useAuth();
  const [syncState, setSyncState] = useState<OfflineSyncState>(INITIAL_STATE);
  const isSyncingRef = useRef(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const getToken = useCallback(async (): Promise<string> => {
    if (!firebaseUser) throw new Error('Not authenticated — please sign in again');
    return firebaseUser.getIdToken(true); // force refresh
  }, [firebaseUser]);

  const doSync = useCallback(async () => {
    if (isSyncingRef.current) return;
    if (!isOnline) return;
    if (!firebaseUser) return;

    const pendingCount = await offlineQueue.countPending();
    if (pendingCount === 0) {
      setSyncState((prev) => ({ ...prev, pendingCount: 0 }));
      return;
    }

    isSyncingRef.current = true;
    setSyncState((prev) => ({ ...prev, isSyncing: true, pendingCount, lastError: null }));

    try {
      const result = await syncPendingItems(getToken, (progress) => {
        setSyncState((prev) => ({ ...prev, progress, pendingCount: progress.total - progress.completed }));
      });

      const remaining = await offlineQueue.countPending();
      setSyncState({
        isSyncing: false,
        pendingCount: remaining,
        lastSyncAt: Date.now(),
        lastError: result.errors.length > 0 ? `${result.errors.length} item(s) failed to sync` : null,
        progress: null,
      });

      // Re-register background sync if items remain
      if (remaining > 0) {
        await registerBackgroundSync();
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Sync failed';
      setSyncState((prev) => ({
        ...prev,
        isSyncing: false,
        lastError: errorMsg,
        progress: null,
      }));
    } finally {
      isSyncingRef.current = false;
    }
  }, [isOnline, firebaseUser, getToken]);

  // Trigger sync when back online
  useEffect(() => {
    if (isOnline) {
      doSync();
    }
  }, [isOnline, doSync]);

  // Polling fallback for browsers without Background Sync API (Safari, Firefox)
  useEffect(() => {
    if (!isOnline) {
      if (pollingRef.current) clearInterval(pollingRef.current);
      return;
    }

    const hasBgSync =
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      'SyncManager' in window;

    if (!hasBgSync) {
      pollingRef.current = setInterval(doSync, 10_000);
      return () => {
        if (pollingRef.current) clearInterval(pollingRef.current);
      };
    }
  }, [isOnline, doSync]);

  // Listen for service worker SYNC_REQUESTED messages
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'SYNC_REQUESTED') {
        doSync();
      }
    };

    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [doSync]);

  // Update pending count when online state changes
  useEffect(() => {
    offlineQueue.countPending().then((count) => {
      setSyncState((prev) => ({ ...prev, pendingCount: count }));
    });
  }, [isOnline]);

  return {
    syncState,
    isOnline,
    doSync,
  };
}
